import type { ITeamMembers } from "@ports/teamMembers/ITeamMembers";
import { generateRandomPassword } from "@domain/Auth";

export interface PasswordGeneratorUseCasesDeps {
	teamMembersApi: ITeamMembers;
	/** Pluggable random-password generator. Defaults to a CSPRNG-backed
	 *  alphanumeric generator using `crypto.getRandomValues`. Tests inject
	 *  a deterministic stub. */
	generatePassword?: () => string;
}

/**
 * Drives the Admin → Password Generator page's "generate + save" workflow.
 * `generateAndSave(teamMemberId)` pairs a freshly-generated password with
 * the API call that persists it; the page surfaces the password to the
 * operator (so they can hand it to the user).
 *
 * Returns the generated string on success — the caller renders it. Errors
 * propagate; the vm catches and toasts.
 */
export interface PasswordGeneratorUseCases {
	generateAndSave(teamMemberId: string): Promise<string>;
}

export async function generateAndSave(deps: PasswordGeneratorUseCasesDeps, teamMemberId: string): Promise<string> {
	const password = (deps.generatePassword ?? generateRandomPassword)();
	await deps.teamMembersApi.setPassword(teamMemberId, password);
	return password;
}

export function makePasswordGeneratorUseCases(deps: PasswordGeneratorUseCasesDeps): PasswordGeneratorUseCases {
	return {
		generateAndSave: (teamMemberId) => generateAndSave(deps, teamMemberId),
	};
}
